/**
 * Draft mode API layer.
 * Combines Firestore CRUD + backend chat/knowledge/export endpoints.
 *
 * Reference files (character-for-character field names):
 *   - reactjs/src/lib/draftChatApi.ts  (chat, knowledge, share, history)
 *   - reactjs/src/api/draftKnowledgeApi.ts (knowledge + utils)
 *   - reactjs/src/lib/userProfile.ts   (Firestore CRUD — via firestore/draft.ts)
 *
 * Backend services:
 *   - mode-agent    :8088  (not used directly from frontend)
 *   - chat-agent    :8090  (DRAFT_CHAT_API_URL)
 *   - modification-agent :8089
 */
import { getFirebaseAuth } from "@/platform/firebase/auth";
import {
  createDraft,
  getDraft,
  getUserDrafts,
  updateDraft,
  deleteDraft,
  countUserDrafts,
} from "@/platform/firebase/firestore/draft";

/** Safely parse JSON response — falls back to text error if body isn't valid JSON. */
async function safeJson(response: Response): Promise<unknown> {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(text || `Server returned ${response.status}`);
  }
}
import type {
  KnowledgeDoc,
  UploadKnowledgeResponse,
  ListKnowledgeResponse,
} from "@/modes/draft/types";

// ─── Config ──────────────────────────────────────────────────────────────────

const DRAFT_CHAT_API_URL = "/api/draft-chat";

// ─── Auth helper ─────────────────────────────────────────────────────────────

async function getAuthToken(): Promise<string> {
  const user = getFirebaseAuth().currentUser;
  if (!user) throw new Error("User not authenticated");
  return user.getIdToken();
}

// ─── Re-export Firestore CRUD ────────────────────────────────────────────────

export { createDraft, getDraft, getUserDrafts, updateDraft, deleteDraft, countUserDrafts };

// ─── Chat API ────────────────────────────────────────────────────────────────

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

export interface ChatResponse {
  message: string;
  summary: string;
  suggested_prompts: string[];
  document?: {
    title: string;
    category: string;
    domain: string;
    description: string;
    sections: Array<{
      title: string;
      content: string;
      subsections?: Array<{ title: string; content: string }>;
      action?: "create" | "update";
      slide_index?: number | null;
      layoutId?: string;
      chart?: {
        type: "bar" | "line" | "pie" | "area";
        title?: string;
        labels: string[];
        datasets: { label: string; values: number[]; color?: string }[];
      };
      processFlow?: {
        direction: "horizontal" | "vertical";
        steps: { label: string; description?: string; color?: string }[];
      };
    }>;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attachment: any;
  token_usage: {
    input_tokens: number;
    output_tokens: number;
    total_tokens: number;
  };
}

export interface SendMessageParams {
  userQuery: string;
  sessionId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  documentContext?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  template?: any;
  mode?: "fast" | "expert" | "ppt_edit";
  stream?: boolean;
  attachment?: File;
}

/** Send a chat message to the draft chat backend. Retries once on 500. */
export async function sendChatMessage(
  params: SendMessageParams
): Promise<ChatResponse> {
  const maxAttempts = 2;
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const formData = new FormData();
    formData.append("user_query", params.userQuery);
    formData.append("session_id", params.sessionId);
    formData.append("mode", params.mode || "fast");
    formData.append("stream", params.stream ? "true" : "false");

    if (params.documentContext) {
      formData.append("document_context", JSON.stringify(params.documentContext));
    }
    if (params.template) {
      formData.append("template", JSON.stringify(params.template));
    }
    if (params.attachment) {
      formData.append("attachment", params.attachment);
    }

    try {
      const response = await fetch(DRAFT_CHAT_API_URL, {
        method: "POST",
        body: formData,
        signal: AbortSignal.timeout(300_000),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({ error: response.statusText }));
        const errorMsg = errorBody.error || response.statusText;
        const status = response.status;

        // Retry on 500 (backend transient error)
        if (status >= 500 && attempt < maxAttempts) {
          console.warn(`[api] Got ${status} on attempt ${attempt}, retrying in 2s...`);
          lastError = new Error(`Chat API error (${status}): ${errorMsg}`);
          await new Promise((r) => setTimeout(r, 2000));
          continue;
        }

        throw new Error(`Chat API error: ${errorMsg}`);
      }

      return safeJson(response) as Promise<ChatResponse>;
    } catch (err) {
      if (err instanceof DOMException && err.name === "TimeoutError") {
        throw new Error("Request timed out. The AI is taking too long — please try a shorter message.");
      }
      if (attempt < maxAttempts && !(err instanceof Error && err.message.startsWith("Chat API error"))) {
        console.warn(`[api] Request failed on attempt ${attempt}, retrying...`, err);
        lastError = err instanceof Error ? err : new Error(String(err));
        await new Promise((r) => setTimeout(r, 2000));
        continue;
      }
      throw err;
    }
  }

  throw lastError || new Error("Request failed after retries");
}

// ─── Batch PPT Generation ─────────────────────────────────────────────────

export interface GenerateBatchParams {
  userQuery: string;
  batchIndex: number;
  totalBatches: number;
  batchSize: number;
  totalSlides: number;
  existingSections: Array<Record<string, unknown>>;
  draftId?: string;
}

export interface GenerateBatchResponse {
  sections: Array<Record<string, unknown>>;
  title: string;
  batchIndex: number;
  totalBatches: number;
}

/** Generate a batch of slides for progressive loading. */
export async function generatePptBatch(
  params: GenerateBatchParams
): Promise<GenerateBatchResponse> {
  const response = await fetch(`${DRAFT_CHAT_API_URL}/generate-ppt-batch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_query: params.userQuery,
      batch_index: params.batchIndex,
      total_batches: params.totalBatches,
      batch_size: params.batchSize,
      total_slides: params.totalSlides,
      existing_sections: params.existingSections,
      draft_id: params.draftId,
    }),
    signal: AbortSignal.timeout(120_000),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Batch generation failed (${response.status})`);
  }

  const data = await safeJson(response) as GenerateBatchResponse;
  return {
    ...data,
    batchIndex: data.batchIndex ?? params.batchIndex,
    totalBatches: data.totalBatches ?? params.totalBatches,
  };
}

// ─── Knowledge API ───────────────────────────────────────────────────────────

/** Build a durable `knowledgeFiles` ref (as stored on the Firestore draft doc)
 * so uploaded files survive browser close/reopen and backend restarts. */
export function toKnowledgeFileRef(file: File, docId: string): import("@/modes/draft/types").KnowledgeFileRef {
  return {
    id: docId,
    file_name: file.name,
    file_type: file.type,
    file_size: file.size,
    chunks_count: 0,
    status: "ready",
    uploaded_at: new Date().toISOString(),
  };
}

/** Upload a knowledge document. */
export async function uploadKnowledgeDocument(
  file: File,
  draftId: string
): Promise<UploadKnowledgeResponse> {
  const token = await getAuthToken();
  const formData = new FormData();
  formData.append("file", file);
  formData.append("draft_id", draftId);

  let response: Response;
  try {
    response = await fetch(`${DRAFT_CHAT_API_URL}/upload-knowledge`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
  } catch (err) {
    console.error("Upload knowledge request failed:", err);
    throw new Error(
      "Upload failed: could not reach the file upload server. Please check your connection and try again."
    );
  }

  if (!response.ok) {
    const body = await response.text();
    let parsed: { error?: string } = {};
    try { parsed = JSON.parse(body); } catch { /* plain text */ }
    throw new Error(parsed.error || body || "Failed to upload document");
  }

  return safeJson(response) as Promise<UploadKnowledgeResponse>;
}

// ─── Deck v2 Knowledge API (direct to chat-agent backend, no proxy) ─────────
// All three functions call the backend directly — same pattern as the canvas
// handleSend which already hits DRAFT_CHAT_API without going through Next.js.

const DECK_V2_BACKEND = process.env.NEXT_PUBLIC_DRAFT_CHAT_API ?? "http://localhost:8090";

async function getDeckV2Token(): Promise<string> {
  try { return (await getAuthToken()) ?? ""; } catch { return ""; }
}

/**
 * Deck v2 upload: FAISS indexing + Anthropic Files API upload in one call.
 */
export async function uploadKnowledgeDocumentV2(
  file: File,
  draftId: string
): Promise<{ status: string; chunks: number; anthropic_file_id?: string }> {
  const token = await getDeckV2Token();
  const formData = new FormData();
  formData.append("file", file);
  formData.append("draft_id", draftId);

  const response = await fetch(`${DECK_V2_BACKEND}/v2/upload-knowledge`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });

  const data = await response.json();
  if (data.error) throw new Error(data.error);
  return data;
}

/**
 * Deck v2 delete: removes file from Anthropic Files API + in-memory store.
 */
export async function deleteKnowledgeDocumentV2(
  draftId: string,
  filename: string
): Promise<void> {
  const token = await getDeckV2Token();
  const response = await fetch(
    `${DECK_V2_BACKEND}/v2/upload-knowledge/${encodeURIComponent(draftId)}?filename=${encodeURIComponent(filename)}`,
    {
      method: "DELETE",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  if (!response.ok) {
    const data = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(data.error || `HTTP ${response.status}`);
  }
}

/**
 * Deck v2: fetch the list of knowledge files stored for a draft.
 * Called on canvas mount to hydrate the Knowledge Body panel.
 */
export async function listKnowledgeFilesV2(
  draftId: string
): Promise<Array<{ file_id: string; filename: string; uploaded_at?: string }>> {
  const token = await getDeckV2Token();
  const response = await fetch(
    `${DECK_V2_BACKEND}/v2/knowledge-files/${encodeURIComponent(draftId)}`,
    {
      method: "GET",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  if (!response.ok) return [];
  const data = await response.json().catch(() => ({})) as { files?: Array<{ file_id: string; filename: string; uploaded_at?: string }> };
  return data?.files ?? [];
}

// ─── Deck v2 Plan API (direct to :8090) ──────────────────────────────────────

export interface DeckPlanSlide {
  id: string;
  slideNumber: number;
  heading: string;
  bullets: string[];
}

export interface DeckPlan {
  title: string;
  slideCount: number;
  narrative: string;
  slides: DeckPlanSlide[];
  status?: string;
  query?: string;
  generated_at?: string;
  last_modified_at?: string;
}

/**
 * GET /v2/deck/plan/{draftId}
 * Returns the stored plan, or null if none exists yet (404).
 * Uses a generous timeout to survive Cloud Run cold starts (up to ~30s).
 */
export async function getDeckPlan(draftId: string): Promise<DeckPlan | null> {
  const token = await getDeckV2Token();
  const response = await fetch(
    `${DECK_V2_BACKEND}/v2/deck/plan/${encodeURIComponent(draftId)}`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: AbortSignal.timeout(600_000),
    }
  );
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`GET plan failed (${response.status})`);
  return response.json() as Promise<DeckPlan>;
}

/**
 * POST /v2/deck/plan
 * Generate a new plan from the user query and persist to Firestore.
 */
export async function createDeckPlan(
  draftId: string,
  userQuery: string
): Promise<DeckPlan> {
  const token = await getDeckV2Token();
  const formData = new FormData();
  formData.append("draft_id", draftId);
  formData.append("user_query", userQuery);

  const response = await fetch(`${DECK_V2_BACKEND}/v2/deck/plan`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
    signal: AbortSignal.timeout(120_000),
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error || `Plan generation failed (${response.status})`);
  }
  return response.json() as Promise<DeckPlan>;
}

/**
 * PATCH /v2/deck/plan/{draftId}
 * Save user edits. Called debounced from DeckPlanScreen.
 */
export async function saveDeckPlan(
  draftId: string,
  plan: DeckPlan
): Promise<void> {
  const token = await getDeckV2Token();
  const response = await fetch(
    `${DECK_V2_BACKEND}/v2/deck/plan/${encodeURIComponent(draftId)}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(plan),
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error || `Plan save failed (${response.status})`);
  }
}

/**
 * POST /v2/deck/plan/{draftId}/approve
 * Mark plan as approved. Backend will use it on the next /v2/deck/build call.
 */
export async function approveDeckPlan(draftId: string): Promise<void> {
  const token = await getDeckV2Token();
  const response = await fetch(
    `${DECK_V2_BACKEND}/v2/deck/plan/${encodeURIComponent(draftId)}/approve`,
    {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error || `Plan approve failed (${response.status})`);
  }
}

// ─── Deck v2 Generation Status API (Phase 5) ─────────────────────────────────

export type GenerationStatus = "pending" | "in_progress" | "complete" | "failed" | "none" | "unknown";

export interface DeckGenerationState {
  generation_id?: string;
  status: GenerationStatus;
  mode?: string;
  user_query?: string;        // stored in Firestore — used to restore chat on refresh
  pptx_url?: string | null;
  slides?: string[] | null;
  slide_count?: number;
  commentary?: string;
  error?: string | null;
  token_usage?: Record<string, number>;
  speaker_notes?: string[];   // per-slide speaker notes extracted from slide_schema
}

/**
 * GET /v2/deck/status/{draftId}/{generationId}
 * Poll a single generation job.
 */
export async function getDeckStatus(
  draftId: string,
  generationId: string,
): Promise<DeckGenerationState> {
  const token = await getDeckV2Token();
  const response = await fetch(
    `${DECK_V2_BACKEND}/v2/deck/status/${encodeURIComponent(draftId)}/${encodeURIComponent(generationId)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error || `Status poll failed (${response.status})`);
  }
  return response.json() as Promise<DeckGenerationState>;
}

/**
 * GET /v2/deck/latest/{draftId}
 * Returns the most recent generation (any status) for this draft.
 * Called on canvas mount to restore state after page refresh.
 * Returns { status: "none" } if no generations exist yet.
 */
export async function getLatestGeneration(draftId: string): Promise<DeckGenerationState> {
  const token = await getDeckV2Token();
  const response = await fetch(
    `${DECK_V2_BACKEND}/v2/deck/latest/${encodeURIComponent(draftId)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!response.ok) return { status: "none" };
  return response.json() as Promise<DeckGenerationState>;
}

/**
 * Load full chat history for a deck v2 draft from the backend.
 * Returns messages ordered oldest → newest.
 * Session ID for deck v2 is the draftId.
 */
export async function getDeckChatHistory(
  draftId: string,
): Promise<{ role: string; message: string; date: string }[]> {
  const token = await getDeckV2Token();
  const response = await fetch(
    `${DECK_V2_BACKEND}/chat-history/${encodeURIComponent(draftId)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!response.ok) return [];
  const data = await response.json() as { messages?: { role: string; message: string; date: string }[] };
  return data.messages ?? [];
}

// ─────────────────────────────────────────────────────────────────────────────

/** List all knowledge documents for a draft. */
export async function listKnowledgeDocuments(
  draftId: string
): Promise<KnowledgeDoc[]> {
  const token = await getAuthToken();
  const response = await fetch(
    `${DRAFT_CHAT_API_URL}/knowledge-docs/${draftId}`,
    { method: "GET", headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok) {
    const body = await response.text();
    let parsed: { error?: string } = {};
    try { parsed = JSON.parse(body); } catch { /* plain text */ }
    throw new Error(parsed.error || body || "Failed to list documents");
  }

  const data = (await safeJson(response)) as ListKnowledgeResponse;
  return data.docs.map((d) => ({
    ...d,
    type: (d as any).file_type || d.type || "unknown",
    name: (d as any).file_name || d.name || "Untitled",
    size: (d as any).file_size || (d as any).size || 0,
  }));
}

/** Delete a knowledge document. */
export async function deleteKnowledgeDocument(
  docId: string,
  draftId?: string,
  fileName?: string,
): Promise<void> {
  const token = await getAuthToken();
  const params = new URLSearchParams();
  if (draftId) params.set("draft_id", draftId);
  if (fileName) params.set("file_name", fileName);
  const qs = params.toString() ? `?${params.toString()}` : "";
  try {
    const response = await fetch(`${DRAFT_CHAT_API_URL}/knowledge-docs/${encodeURIComponent(docId)}${qs}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const body = await response.text();
      let parsed: { error?: string } = {};
      try { parsed = JSON.parse(body); } catch { /* plain text */ }
      console.warn("Delete knowledge doc failed:", parsed.error || body);
    }
  } catch (err) {
    console.warn("Delete knowledge doc request failed:", err);
  }
}

// ─── Chat history ────────────────────────────────────────────────────────────

/** Get conversation history for a session. */
export async function getChatHistory(sessionId: string): Promise<(ChatMessage & { id: string })[]> {
  console.log(`[ChatHistory] Fetching history for session: ${sessionId}`);
  const response = await fetch(
    `${DRAFT_CHAT_API_URL}/chat-history/${sessionId}`,
    { signal: AbortSignal.timeout(15_000) }
  );

  if (!response.ok) {
    console.error(`[ChatHistory] Response not OK: ${response.status} ${response.statusText}`);
    throw new Error(`Get history failed: ${response.statusText}`);
  }

  const data = (await safeJson(response)) as { messages?: unknown[] };
  const rawMessages = data.messages || [];
  console.log(`[ChatHistory] Raw messages count: ${rawMessages.length}`);
  const mapped = rawMessages.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (msg: any) => ({
      id: msg.uuid || msg.id || Date.now().toString(),
      role: msg.role === "model" ? "assistant" : msg.role,
      content: msg.message,
      timestamp: new Date(msg.date),
    })
  );
  console.log(`[ChatHistory] Mapped messages count: ${mapped.length}, first role: ${mapped[0]?.role}, first content length: ${mapped[0]?.content?.length ?? 0}`);
  return mapped;
}

// ─── Export ──────────────────────────────────────────────────────────────────

interface ExportPdfPayload {
  title: string;
  sections: Array<{ title: string; content: string }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  theme: any;
  watermark?: boolean;
}

/** Export document as PDF via backend. Returns a Blob. */
export async function exportPdf(payload: ExportPdfPayload): Promise<Blob> {
  const response = await fetch(`${DRAFT_CHAT_API_URL}/export/pdf`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || `Server returned ${response.status}`);
  }

  return response.blob();
}

interface ExportPptPayload {
  title: string;
  slides: Array<{
    layoutId: string;
    themeId: string;
    title: string;
    subtitle: string;
    content: string;
    image?: string;
    notes: string;
  }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  themes: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  layouts: any[];
}

/** Export slides as PPTX via backend. Returns a Blob. */
export async function exportPptx(payload: ExportPptPayload): Promise<Blob> {
  const response = await fetch(`${DRAFT_CHAT_API_URL}/export-ppt`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw new Error("Export failed");
  return response.blob();
}

// ─── Share links ─────────────────────────────────────────────────────────────

/** Create a share link for a draft with full document data. */
export async function createShareLink(
  draftId: string,
  docData: {
    title: string;
    description?: string;
    sections: unknown[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    templateData?: any;
  }
): Promise<{ shareUrl: string; token: string }> {
  const response = await fetch(`${DRAFT_CHAT_API_URL}/share-link/${draftId}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(docData),
  });

  if (!response.ok) {
    throw new Error(`Share link creation failed: ${response.statusText}`);
  }

  return safeJson(response) as Promise<{ shareUrl: string; token: string }>;
}

/** Get shared draft data via token (public, no auth required). */
export async function getSharedDraft(token: string): Promise<unknown> {
  const response = await fetch(`${DRAFT_CHAT_API_URL}/shared/${token}`);
  if (!response.ok) {
    throw new Error(`Shared draft fetch failed: ${response.statusText}`);
  }
  return safeJson(response);
}

// ─── Utility functions (from draftKnowledgeApi.ts) ──────────────────────────

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatUploadDate(isoDate: string): string {
  if (!isoDate) return "Just now";
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return "Just now";
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function getFileTypeLabel(fileType: string): string {
  if (!fileType) return "FILE";
  const t = fileType.toLowerCase();
  if (t.includes("pdf")) return "PDF";
  if (t.includes("docx") || (t.includes("word") && t.includes("document"))) return "DOCX";
  if (t.includes("doc") && !t.includes("docx")) return "DOC";
  if (t.includes("pptx") || t.includes("presentation") || t.includes("powerpoint")) return "PPTX";
  if (t.includes("ppt") && !t.includes("pptx")) return "PPT";
  if (t.includes("txt") || t.includes("text")) return "TXT";
  if (t.includes("xlsx") || t.includes("spreadsheet")) return "XLSX";
  if (t.includes("xls") && !t.includes("xlsx")) return "XLS";
  if (t.includes("csv")) return "CSV";
  if (t.includes("png")) return "PNG";
  if (t.includes("jpg") || t.includes("jpeg")) return "JPEG";
  if (t.includes("gif")) return "GIF";
  if (t.includes("webp")) return "WebP";
  return "FILE";
}
